import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { useTranslatedRoutes } from '@/routes';
import { checkIsParent } from '@/utils/helpers/common';
import { Link, useLocation } from 'react-router-dom';
import { IMyInfo } from '@/services/user/types';
import { getRegionConfig } from '@/utils/helpers/region';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import CustomSidebarItem from './CustomSidebarItem';

export function CustomSidebar({ myInfo }: { myInfo: IMyInfo }) {
  const [openMenus, setOpenMenus] = useState<string[]>([]);

  const { t } = useTranslation();

  const region = getRegionConfig();
  const routes = useTranslatedRoutes({ isParent: checkIsParent(), myInfo });
  const location = useLocation();

  const isActive = useCallback(
    (path: string) => {
      if (path === '/') {
        return location.pathname === '/';
      }
      return location.pathname.startsWith(path);
    },
    [location.pathname]
  );

  const isSubActive = useCallback(
    (path: string) => {
      return (location.pathname + location.search).includes(path);
    },
    [location.pathname, location.search]
  );

  useEffect(() => {
    const activeRouteWithSubs = routes.find(
      (r) => r.subItems && r.subItems.length > 0 && isActive(r.path)
    );
    if (activeRouteWithSubs) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOpenMenus((prev) => {
        if (prev.includes(activeRouteWithSubs.path)) return prev;
        return [...prev, activeRouteWithSubs.path];
      });
    }
  }, [location.pathname, routes, isActive]);

  const toggleMenu = (path: string) => {
    setOpenMenus((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const groupedRoutes = routes.reduce(
    (acc, route) => {
      const section = route.section || 'main';
      if (!acc[section]) {
        acc[section] = [];
      }
      acc[section].push(route);
      return acc;
    },
    {} as Record<string, typeof routes>
  );

  const sections = [
    { key: 'main', label: null },
    { key: 'digital-school', label: t('routes.sections.digital-school') },
    { key: 'management', label: t('routes.sections.management') },
  ];

  const renderRoute = (route: (typeof routes)[0]) => (
    <CustomSidebarItem
      key={route.path}
      route={route}
      isActive={isActive}
      isSubActive={isSubActive}
      openMenus={openMenus}
      toggleMenu={toggleMenu}
    />
  );

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="flex justify-center p-6 relative">
        <Link to="/">
          <img
            src={region.logo.src}
            width={region.logo.width}
            height={region.logo.height}
            alt={region.name}
          />
        </Link>
        <SidebarTrigger className="absolute top-1/2 -right-3.5 transform -translate-y-1/2" />
      </SidebarHeader>
      <SidebarContent>
        {sections.map(({ key, label }) =>
          groupedRoutes[key] ? (
            <SidebarGroup key={key}>
              {label && <SidebarGroupLabel>{label}</SidebarGroupLabel>}
              <SidebarMenu>{groupedRoutes[key].map(renderRoute)}</SidebarMenu>
            </SidebarGroup>
          ) : null
        )}
      </SidebarContent>
      <SidebarFooter />
    </Sidebar>
  );
}
